// Reimplementación en el carrito custom de la lógica de "regalo gratis" que antes
// vivía en las extensiones de Shopify (free-gift-auto-add, free-gift-discount,
// free-gift-code-trigger, free-gift-category-trigger), que dependían del
// carrito/checkout nativo y por eso no se ejecutaban con este carrito custom.
(function () {
  const MAX_COLLECTION_LOOKUPS = 25;

  let evaluationToken = 0;
  let isBusy = false;
  let queuedRerun = false;

  // Firma del carrito para el que la Function ya rechazó el regalo (ver
  // `dropGiftLineIfCharged`). Sin esto, retirar la línea cobrada dispararía una
  // nueva evaluación que volvería a añadirla: bucle infinito de alta y baja.
  // Cualquier cambio real del carrito cambia la firma y se vuelve a intentar.
  let rejectedSignature = null;

  // Regalos principales que ya han fallado al añadirse en un mercado (sin stock,
  // normalmente), por "mercado|variante". Es lo que distingue una reserva que
  // está bien puesta —el principal de ESTE mercado no tenía stock— de una que
  // hay que cambiar: al pasar de EE.UU. a España, el regalo principal de EE.UU.
  // puede ser justo la reserva de España, y si España tiene stock de su
  // principal, lo que toca es el principal.
  //
  // Se guarda en sessionStorage durante un rato y no solo en memoria: la
  // página se recarga al volver del checkout, y sin esto se reintentaba el
  // principal agotado en cada vuelta, con su alta y su baja —y su evento de
  // "añadido al carrito" en la analítica—. Caduca para que, si vuelve el stock,
  // el cliente acabe recibiendo el principal. Si el almacenamiento no está
  // disponible (modo privado, bloqueado), funciona solo en memoria.
  const UNAVAILABLE_STORAGE_KEY = "free-gift:main-unavailable";
  const UNAVAILABLE_TTL_MS = 30 * 60 * 1000;
  const mainGiftUnavailable = loadMainGiftUnavailable();

  function loadMainGiftUnavailable() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(UNAVAILABLE_STORAGE_KEY) || "{}");
      const now = Date.now();
      return new Map(Object.entries(stored).filter(([, at]) => now - at < UNAVAILABLE_TTL_MS));
    } catch (error) {
      return new Map();
    }
  }

  function isMainGiftUnavailable(config) {
    const at = mainGiftUnavailable.get(mainGiftKey(config));
    return at !== undefined && Date.now() - at < UNAVAILABLE_TTL_MS;
  }

  function markMainGiftUnavailable(config) {
    mainGiftUnavailable.set(mainGiftKey(config), Date.now());
    try {
      sessionStorage.setItem(
        UNAVAILABLE_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(mainGiftUnavailable))
      );
    } catch (error) {
      // Sin almacenamiento, basta con la memoria de esta página.
    }
  }

  // Versión del formato de `custom.free_gift_config` que entiende este fichero;
  // la misma que la Function y la extensión de checkout.
  const CONFIG_VERSION = 2;

  // La configuración sale de `custom.free_gift_config`, un JSON en el Shop que es
  // la fuente única: lo leen igual la Shopify Function y la extensión de
  // checkout. head-init.liquid lo pasa en crudo junto al mercado actual, y aquí
  // se resuelve con las mismas reglas que ellas para que no puedan discrepar:
  //
  // - interruptor global y del mercado;
  // - cada campo del mercado funciona como el "usar valor por defecto" de
  //   PrestaShop: si la clave no está se usa la del `default`, y si está manda
  //   la del mercado — también vacía, que apaga esa condición en ese mercado;
  // - todas las condiciones con valor se exigen a la vez.
  //
  // Devuelve null cuando no hay nada que añadir: regla apagada, JSON ausente,
  // ilegible o de otra versión. La red de seguridad que retira un regalo cobrado
  // no depende de esto.
  // Mensajes de diagnóstico en consola: solo con `debug: true` en
  // custom.free_gift_config (brownie-tools → Regalo gratis → Mensajes de
  // consola). Los errores de configuración (JSON ilegible, fecha mal escrita)
  // salen siempre con console.error: son fallos, no diagnóstico.
  function isDebug() {
    let raw = window.freeGiftSource && window.freeGiftSource.raw;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (error) {
        return false;
      }
    }
    return Boolean(raw && raw.debug === true);
  }

  function debugLog(...args) {
    if (isDebug()) console.warn(...args);
  }

  function getConfig() {
    const source = window.freeGiftSource;
    let raw = source && source.raw;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (error) {
        console.error("[free-gift] custom.free_gift_config ilegible", error);
        return null;
      }
    }
    if (!raw || typeof raw !== "object") return null;
    if (raw.version !== CONFIG_VERSION) {
      console.error(
        `[free-gift] custom.free_gift_config es de la versión ${raw.version}; se esperaba la ${CONFIG_VERSION}`
      );
      return null;
    }
    if (!isEnabled(raw.enabled)) return null;

    const own = (raw.markets || {})[source.marketId] || {};
    if (!isEnabled(own.enabled)) return null;

    const merged = Object.assign({}, raw.default || {}, withoutMetaKeys(own));

    // Una fecha mal escrita falla cerrado, igual que en la Function: ninguno da
    // el regalo y no hay desacuerdo.
    const startsAt = parseInstant(merged.startsAt);
    const endsAt = parseInstant(merged.endsAt);
    if (startsAt === undefined || endsAt === undefined) {
      console.error(
        `[free-gift] fecha ilegible (startsAt=${merged.startsAt}, endsAt=${merged.endsAt}); formato esperado 2026-10-01T20:00:00+01:00`
      );
      return null;
    }

    // El JSON guarda GIDs, y la Ajax API y /cart.js trabajan con ids numéricos.
    return {
      variantId: numericId(merged.variant),
      // Inicio y fin exactos, en milisegundos. La Function solo comprueba el
      // día; la hora la controlan el theme y la extensión de checkout.
      startsAt,
      endsAt,
      // Para cuando el principal no tiene stock.
      fallbackVariantId: numericId(merged.fallbackVariant),
      // Para recordar por mercado qué regalo principal ha fallado.
      marketId: source.marketId || null,
      country: source.country || null,
      channels: isBlank(merged.channels) ? null : merged.channels,
      minAmount: isBlank(merged.minAmount) ? 0 : Number(merged.minAmount),
      requiredProductVariantId: numericId(merged.requiredProduct),
      categoryId: numericId(merged.category),
      discountCode: isBlank(merged.triggerCode) ? null : String(merged.triggerCode).trim(),
    };
  }

  // Solo `false` apaga (booleano o texto, por si se escribe a mano). Un campo que
  // falta cuenta como encendido. Mismo criterio que la Function y la extensión.
  function isEnabled(value) {
    if (value === false) return false;
    return String(value ?? "").trim().toLowerCase() !== "false";
  }

  // `name` y `enabled` describen el mercado, no son parte de la regla.
  function withoutMetaKeys(market) {
    const rule = {};
    Object.keys(market).forEach((key) => {
      if (key !== "name" && key !== "enabled") rule[key] = market[key];
    });
    return rule;
  }

  function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === "";
  }

  function numericId(value) {
    if (isBlank(value)) return null;
    return String(value).split("/").pop();
  }

  // Fecha y hora con zona horaria (2026-10-01T20:00:00+01:00, o con Z). Mismo
  // formato que aceptan la Function y la extensión de checkout.
  const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

  // Milisegundos; null = sin límite; undefined = mal escrita. Al llevar la zona
  // horaria es un instante absoluto: las 20:00 de Lisboa son las 21:00 de
  // Madrid, esté el cliente donde esté.
  function parseInstant(value) {
    if (isBlank(value)) return null;
    const text = String(value).trim();
    if (!INSTANT_PATTERN.test(text)) return undefined;
    const ms = Date.parse(text);
    return Number.isFinite(ms) ? ms : undefined;
  }

  function isWithinWindow(config, now) {
    if (config.startsAt !== null && now < config.startsAt) return false;
    if (config.endsAt !== null && now > config.endsAt) return false;
    return true;
  }

  // Si el cliente tiene la página abierta justo cuando empieza o acaba la
  // campaña, nada del carrito cambia y nadie la reevaluaría: se programa una
  // pasada para ese momento. Solo si es en las próximas 24 horas.
  let boundaryTimer = null;
  function scheduleBoundaryRerun(config) {
    if (boundaryTimer) clearTimeout(boundaryTimer);
    boundaryTimer = null;
    const now = Date.now();
    const upcoming = [config.startsAt, config.endsAt].filter((at) => at !== null && at > now);
    if (!upcoming.length) return;
    const delay = Math.min(...upcoming) - now + 1000;
    if (delay > 24 * 60 * 60 * 1000) return;
    boundaryTimer = setTimeout(evaluate, delay);
  }

  // Los códigos que ha introducido el cliente, y solo esos.
  //
  // Es lo mismo que mira la Shopify Function (`enteredDiscountCodes`), y aquí
  // hay que mirar lo mismo o theme y Function discreparán — con el cliente
  // pagando un producto etiquetado como regalo.
  //
  // No se miran los TÍTULOS de las aplicaciones de descuento, aunque el de un
  // descuento de código sea el propio código. Daban dos problemas serios:
  //
  //  - El descuento del regalo se titula "Regalo", y en España el código
  //    configurado era `REGALO`: el regalo se justificaba a sí mismo.
  //  - Esos títulos aparecen y desaparecen según esté o no la línea del regalo,
  //    así que la firma del carrito cambiaba al añadirla, la memoria anti-bucle
  //    no reconocía el estado ya rechazado, y el theme entraba en alta y baja
  //    sin fin.
  //
  // `applicable` se ignora a propósito: un código cuyo único efecto fuera el
  // regalo no es aplicable mientras la línea no esté en el carrito, que es justo
  // lo que se está decidiendo. Contar uno de más lo cubre `dropGiftLineIfCharged`,
  // que además protege de cualquier otra discrepancia con la Function.
  function enteredDiscountCodes(cart) {
    return (cart.discount_codes || [])
      .filter((entry) => entry && entry.code)
      .map((entry) => String(entry.code).trim().toUpperCase());
  }

  function isDiscountCodeApplied(cart, discountCode) {
    const target = (discountCode || "").trim().toUpperCase();
    // Sin código configurado no se concede: un modo `_code` sin código es una
    // configuración incompleta, y la Function la trata igual.
    if (!target) return false;
    return enteredDiscountCodes(cart).includes(target);
  }

  // Contrato compartido con Hydrogen y con la Shopify Function
  // `free-gift-discount`: la línea del regalo se marca con esta propiedad, y es
  // así como la Function sabe cuál poner a 0 €. El nombre es histórico (lo fijó
  // Hydrogen). El prefijo `_` la oculta al cliente en carrito y checkout.
  //
  // Se identifica por la marca y NO por la variante: según campaña y mercado el
  // regalo puede ser un producto normal de catálogo, así que una unidad que el
  // cliente compre por su cuenta debe cobrarse. La Ajax API solo fusiona líneas
  // cuando coinciden variante Y properties, de modo que ambas conviven.
  const GIFT_PROPERTY = "_hydrogen_gift";

  function isGiftLine(item) {
    return (item.properties || {})[GIFT_PROPERTY] === "true";
  }

  function findGiftLine(cart) {
    return (cart.items || []).find(isGiftLine) || null;
  }

  function productLines(cart) {
    return (cart.items || []).filter((item) => !isGiftLine(item));
  }

  // Lo que la Function sabe de cada producto y /cart.js no trae: si no computa
  // como valor de carrito (`custom.no_cart_count`: totebags, envoltorios) y en
  // qué colecciones está. Se cachea por producto para toda la visita: ninguna de
  // las dos cosas cambia mientras el cliente navega, y así no hay una consulta
  // por cada cambio del carrito.
  const productFacts = new Map(); // product_id -> { noCartCount, collections }

  async function loadProductFacts(lines) {
    const missing = [...new Set(lines.map((item) => String(item.product_id)))]
      .filter((id) => !productFacts.has(id))
      .slice(0, MAX_COLLECTION_LOOKUPS);
    if (!missing.length) return;

    // La Storefront API pública no tiene `Product.inCollection` (solo existe en
    // la Admin API), así que la pertenencia se comprueba listando las
    // colecciones del producto, como hace wishlist.js.
    const query = `{ ${missing
      .map(
        (id, index) =>
          `p${index}: product(id: "gid://shopify/Product/${id}") {
            noCartCount: metafield(namespace: "custom", key: "no_cart_count") { value }
            collections(first: 50) { nodes { id } }
          }`
      )
      .join(" ")} }`;

    try {
      const response = await fetch(`${window.shopUrl}/api/2025-10/graphql.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": window.config?.storefrontAccessToken || "",
        },
        body: JSON.stringify({ query }),
      });
      const json = await response.json();
      const data = (json && json.data) || {};
      missing.forEach((id, index) => {
        const node = data[`p${index}`];
        // Un producto que no llega no se cachea: se reintentará en la siguiente
        // evaluación en vez de quedarse con un "no sé" para toda la visita.
        if (!node) return;
        productFacts.set(id, {
          noCartCount: isNoCartCount(node.noCartCount?.value),
          collections: (node.collections?.nodes || []).map((collection) => collection.id),
        });
      });
    } catch (error) {
      console.error("[free-gift] no se pudieron leer los productos del carrito", error);
    }
  }

  // Descuentos de pedido (un código de 10 % sobre el total, por ejemplo). Se
  // restan enteros aunque parte caiga sobre totebags: mejor quedarse cortos que
  // dar el regalo a un pedido que no llega.
  function orderDiscountCents(cart) {
    return (cart.cart_level_discount_applications || []).reduce(
      (sum, discount) => sum + (Number(discount.total_allocated_amount) || 0),
      0
    );
  }

  // Mismo parseo que la Function: explícito a propósito, porque el texto
  // "false" es truthy en JS.
  function isNoCartCount(value) {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }

  // Devuelve { eligible, anchor, stale }. `stale` indica que el carrito cambió
  // mientras la consulta de productos estaba en vuelo y hay que ignorar el
  // resultado.
  //
  // Mismas condiciones que la Function, y todas las que tengan valor a la vez.
  async function evaluateEligibility(cart, config, token) {
    const lines = productLines(cart);
    if (!lines.length) return { eligible: false, anchor: null };

    // Fuera de la ventana de la campaña no hay regalo, y si ya había uno se
    // retira: la Function lo seguiría concediendo hasta que cambie el día.
    if (!isWithinWindow(config, Date.now())) return { eligible: false, anchor: null };

    await loadProductFacts(lines);
    if (token !== evaluationToken) return { eligible: false, anchor: null, stale: true };

    // Como en la Function, los productos que no computan (totebags, envoltorios)
    // ni suman al mínimo ni cuentan como producto real: un carrito con solo un
    // totebag no da regalo. Si no se pudo saber, se cuentan (optimista); en ese
    // caso decide la Function y, si deniega, `dropGiftLineIfCharged` lo retira.
    const realLines = lines.filter(
      (item) => !productFacts.get(String(item.product_id))?.noCartCount
    );
    const anchor = realLines[0] || null;
    if (!anchor) return { eligible: false, anchor: null };

    if (config.discountCode && !isDiscountCodeApplied(cart, config.discountCode)) {
      return { eligible: false, anchor };
    }

    if (config.minAmount > 0) {
      // El mínimo se mide sobre lo que se paga: con los descuentos de línea
      // (`final_line_price`) y los del pedido ya restados. La Function no puede
      // hacerlo —Shopify calcula todos los descuentos a la vez y ella no ve los
      // demás—, así que aquí y en el checkout se es más estricto que ella.
      // El mínimo va en unidades de moneda (60 = 60 €) y /cart.js da céntimos.
      const totalCents = realLines.reduce((sum, item) => sum + item.final_line_price, 0);
      if ((totalCents - orderDiscountCents(cart)) / 100 < config.minAmount) {
        return { eligible: false, anchor };
      }
    }

    if (
      config.requiredProductVariantId &&
      !realLines.some((item) => String(item.id) === String(config.requiredProductVariantId))
    ) {
      return { eligible: false, anchor };
    }

    if (config.categoryId) {
      const collectionGid = `gid://shopify/Collection/${config.categoryId}`;
      const inCategory = realLines.some((item) =>
        (productFacts.get(String(item.product_id))?.collections || []).includes(collectionGid)
      );
      if (!inCategory) return { eligible: false, anchor };
    }

    return { eligible: true, anchor };
  }

  function addGiftLine(variantId) {
    return fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { [GIFT_PROPERTY]: "true" },
      }),
    });
  }

  // El cliente ya tiene en el carrito, pagado, el mismo producto que le toca
  // de regalo (p. ej. se puso una totebag y después añade el bolso que da
  // derecho a una totebag gratis): en vez de añadir otra, se convierte una
  // unidad de la suya en el regalo.
  //
  // Solo si el carrito sigue cumpliendo contando esa unidad como regalo, que es
  // como lo ve la Function (el regalo nunca cuenta para darse derecho a sí
  // mismo). Si no —p. ej. el reloj es lo que hace llegar al mínimo—, la unidad
  // se queda pagada y el regalo se añade aparte, como siempre.
  //
  // Devuelve true si ha convertido, false si no toca, o "stale" si el carrito
  // cambió mientras se decidía.
  async function convertPaidLineToGift(cart, config, token) {
    // La reserva solo cuando el principal ha fallado en este mercado: es la
    // misma regla que al añadir.
    const candidates = [config.variantId];
    if (config.fallbackVariantId && isMainGiftUnavailable(config)) {
      candidates.push(config.fallbackVariantId);
    }
    for (const variantId of candidates.filter(Boolean)) {
      const item = productLines(cart).find((line) => String(line.id) === String(variantId));
      if (!item) continue;

      const result = await evaluateEligibility(cartWithOneUnitAsGift(cart, item), config, token);
      if (result.stale) return "stale";
      if (!result.eligible) continue;

      debugLog(`[free-gift] el cliente ya tiene ${variantId} pagado: se convierte una unidad en el regalo`);
      const response = await markOneUnitAsGift(item);
      return response.ok;
    }
    return false;
  }

  // El carrito tal como quedaría con una unidad de `item` como regalo, para
  // evaluarlo con las mismas reglas.
  function cartWithOneUnitAsGift(cart, item) {
    const remaining = item.quantity - 1;
    const scale = (value) =>
      typeof value === "number" ? Math.round((value * remaining) / item.quantity) : value;
    const items = (cart.items || [])
      .map((line) =>
        line === item
          ? {
            ...line,
            quantity: remaining,
            final_line_price: scale(line.final_line_price),
            line_price: scale(line.line_price),
            original_line_price: scale(line.original_line_price),
          }
          : line
      )
      .filter((line) => line.quantity > 0);
    items.push({
      ...item,
      quantity: 1,
      final_line_price: 0,
      line_price: 0,
      properties: { ...(item.properties || {}), [GIFT_PROPERTY]: "true" },
    });
    return { ...cart, items };
  }

  // Con una sola unidad se marca la propia línea; con más, se baja una y se
  // añade la del regalo.
  async function markOneUnitAsGift(item) {
    if (item.quantity === 1) {
      return fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: item.key,
          quantity: 1,
          properties: { ...(item.properties || {}), [GIFT_PROPERTY]: "true" },
        }),
      });
    }
    await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: item.key, quantity: item.quantity - 1 }),
    });
    return addGiftLine(item.id);
  }

  // Deshace una conversión que la Function no ha aceptado: la unidad vuelve a
  // ser un producto normal pagado. No se borra, porque la había metido el
  // cliente.
  function unmarkGiftLine(line) {
    const properties = { ...(line.properties || {}) };
    delete properties[GIFT_PROPERTY];
    return fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
    });
  }

  // Añade el regalo principal y, si está agotado, el de reserva. Devuelve si
  // alguno ha entrado.
  //
  // El stock se pregunta antes: no basta con que la Ajax API rechace el alta,
  // porque a veces la acepta y es el checkout quien después dice que está
  // agotado, con su popup, y lo retira.
  async function addGiftWithFallback(config) {
    const candidates = [config.variantId, config.fallbackVariantId].filter(
      (id, index, list) => id && list.indexOf(id) === index
    );
    const available = await fetchGiftAvailability(config, candidates);
    for (const variantId of candidates) {
      const isMain = String(variantId) === String(config.variantId);
      // Ya consta como agotado en este mercado durante esta visita.
      if (isMain && isMainGiftUnavailable(config)) continue;
      if (available.get(String(variantId)) === false) {
        debugLog(`[free-gift] el regalo ${variantId} está agotado, no se añade`);
        if (String(variantId) === String(config.variantId)) {
          markMainGiftUnavailable(config);
        }
        continue;
      }
      if (await tryAddGift(config, variantId)) return true;
    }
    return false;
  }

  // Stock de una línea del carrito según la propia tienda online: true, false,
  // o null si no se ha podido saber. No se cachea: es justo lo que cambia a
  // mitad de campaña.
  //
  // Se usa /products/<handle>.js y no la Storefront API porque el token del
  // theme no ve todos los productos (devuelve null para los que no están
  // publicados en su canal), y este endpoint es la misma vista que usan el
  // carrito y el checkout. /cart.js ya trae el handle de cada línea.
  async function fetchLineAvailability(line) {
    if (!line.handle) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    try {
      const response = await fetch(`${root}products/${line.handle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      const variant = (product.variants || []).find((item) => String(item.id) === String(line.id));
      return variant ? variant.available === true : null;
    } catch (error) {
      console.error("[free-gift] no se pudo consultar el stock del regalo del carrito", error);
      return null;
    }
  }

  // Stock actual de las variantes del regalo, por id numérico, antes de
  // añadirlas. Se pregunta en el contexto del país del cliente.
  //
  // Es una ayuda, no la garantía: el token del theme no ve todos los productos
  // y para esos devuelve null ("no se sabe"). En ese caso se intenta el alta, y
  // quien decide es /cart/add.js, que rechaza lo agotado con un error.
  async function fetchGiftAvailability(config, variantIds) {
    const availability = new Map();
    const ids = variantIds.filter(Boolean).map((id) => `gid://shopify/ProductVariant/${id}`);
    if (!ids.length) return availability;

    const context = config.country ? `@inContext(country: ${config.country})` : "";
    const query = `query FreeGiftAvailability($ids: [ID!]!) ${context} {
      nodes(ids: $ids) { ... on ProductVariant { id availableForSale } }
    }`;
    try {
      const response = await fetch(`${window.shopUrl}/api/2025-10/graphql.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": window.config?.storefrontAccessToken || "",
        },
        body: JSON.stringify({ query, variables: { ids } }),
      });
      const json = await response.json();
      ((json && json.data && json.data.nodes) || []).forEach((node) => {
        if (node && node.id) availability.set(numericId(node.id), node.availableForSale === true);
      });
    } catch (error) {
      console.error("[free-gift] no se pudo consultar el stock del regalo", error);
    }
    return availability;
  }

  // Intenta añadir una variante como regalo. Si es el principal y falla, lo
  // apunta para este mercado: así la reserva que entre después se queda, en vez
  // de intentar volver al principal en cada cambio del carrito.
  async function tryAddGift(config, variantId) {
    const isMain = String(variantId) === String(config.variantId);
    const response = await addGiftLine(variantId);
    if (response.ok) {
      // La Ajax API de esta tienda acepta a veces una variante agotada, y
      // entonces es el checkout quien la rechaza con su popup. Se comprueba lo
      // que se acaba de añadir —la respuesta trae su handle— y, si está
      // agotado, se quita en esta misma pasada: el carrito no se repinta hasta
      // el final, así que el cliente no llega a verlo.
      const item = await response.json().catch(() => null);
      if (item && (await fetchLineAvailability(item)) === false) {
        debugLog(`[free-gift] el regalo ${variantId} se aceptó pero está agotado: se quita`);
        await removeGiftLine(item);
        if (isMain) markMainGiftUnavailable(config);
        return false;
      }
      return true;
    }
    if (isMain) {
      markMainGiftUnavailable(config);
    }
    const body = await response.json().catch(() => ({}));
    debugLog(
      `[free-gift] no se pudo añadir el regalo ${variantId} (HTTP ${response.status}): ${
        body.description || body.message || "sin detalle"
      }`
    );
    return false;
  }

  function mainGiftKey(config) {
    return `${config.marketId}|${config.variantId}`;
  }

  function isConfiguredGift(line, config) {
    const id = String(line.id);
    return (
      id === String(config.variantId) ||
      (config.fallbackVariantId !== null && id === String(config.fallbackVariantId))
    );
  }

  // El regalo puesto es la reserva de este mercado, y no consta que su
  // principal haya fallado aquí: normalmente porque el cliente viene de otro
  // mercado cuyo regalo principal es justo esta reserva. Toca el principal.
  function shouldUpgradeToMainGift(line, config) {
    return (
      config.fallbackVariantId !== null &&
      String(config.fallbackVariantId) !== String(config.variantId) &&
      String(line.id) === String(config.fallbackVariantId) &&
      !isMainGiftUnavailable(config)
    );
  }

  // Se añade primero el principal y se quita después la reserva: si el
  // principal no entra, el cliente sigue teniendo su regalo.
  async function upgradeToMainGift(cart, fallbackLine, config) {
    const hasMain = (cart.items || []).some(
      (item) => isGiftLine(item) && String(item.id) === String(config.variantId)
    );
    if (!hasMain) {
      // Sin stock del principal la reserva está bien puesta: se deja, y no se
      // vuelve a preguntar en este mercado.
      const available = await fetchGiftAvailability(config, [config.variantId]);
      if (available.get(String(config.variantId)) === false) {
        markMainGiftUnavailable(config);
        return;
      }
      if (!(await tryAddGift(config, config.variantId))) return;
    }
    await removeGiftLine(fallbackLine);
  }

  // El regalo puesto se ha agotado desde que se añadió. La línea no desaparece
  // sola del carrito: Shopify solo se da cuenta al entrar al checkout, y ahí
  // sale su popup de "Agotado". Se cambia aquí por el siguiente con stock para
  // que el cliente no llegue a verlo.
  async function replaceSoldOutGift(giftLine, config) {
    if ((await fetchLineAvailability(giftLine)) !== false) return false;

    debugLog(`[free-gift] el regalo ${giftLine.id} del carrito se ha agotado, se cambia`);
    if (String(giftLine.id) === String(config.variantId)) {
      markMainGiftUnavailable(config);
    }
    await removeGiftLine(giftLine);
    // Recorre principal y reserva saltándose los agotados, incluido el que se
    // acaba de quitar. Si ninguno tiene stock, el cliente se queda sin regalo en
    // vez de con uno agotado.
    await addGiftWithFallback(config);
    return true;
  }

  function removeGiftLine(giftLine) {
    return fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
    });
  }

  // Por variante y no por línea: la misma cantidad repartida en dos líneas
  // (p. ej. tras deshacer la conversión de una unidad en regalo) es el mismo
  // carrito, y no debe reintentar lo que la Function ya rechazó.
  function cartSignature(cart) {
    const byVariant = new Map();
    productLines(cart).forEach((item) => {
      const id = String(item.id);
      byVariant.set(id, (byVariant.get(id) || 0) + item.quantity);
    });
    const lines = [...byVariant.entries()]
      .map(([id, quantity]) => `${id}:${quantity}`)
      .sort()
      .join(",");
    const codes = enteredDiscountCodes(cart).sort().join(",");
    return `${lines}|${codes}`;
  }

  // Red de seguridad del contrato con la Shopify Function `free-gift-discount`.
  //
  // Theme y Function evalúan las mismas reglas por separado, y la única forma de
  // que el cliente salga perjudicado es que el theme añada la línea y la
  // Function la deniegue: entonces paga a precio completo un producto que el
  // carrito le presenta como regalo. Es peor que no dárselo. Así que se
  // comprueba que la línea está a 0 y, si no, se retira.
  //
  // Cubre cualquier discrepancia, no solo las previstas: descuento mal
  // configurado o sin desplegar, espejo de configuración desfasado, mercado sin
  // variante, o el código de un modo `_code` que la Function no llega a ver.
  function isGiftLineCharged(giftLine) {
    // Campo estable de /cart.js; si algún día cambiara de forma preferimos no
    // tocar nada antes que retirar todos los regalos legítimos.
    if (typeof giftLine.final_line_price !== "number") {
      console.error("[free-gift] no se puede verificar el precio del regalo");
      return false;
    }
    return giftLine.final_line_price !== 0;
  }

  async function dropGiftLine(giftLine, signature) {
    console.error(
      "[free-gift] la Function no ha aplicado el descuento: se retira el regalo para no cobrárselo al cliente",
      { variantId: giftLine.id, finalLinePrice: giftLine.final_line_price }
    );
    rejectedSignature = signature;
    await removeGiftLine(giftLine);
  }

  // Tras añadir la línea hay que releer el carrito: el precio con descuento lo
  // calcula Shopify, no lo sabemos hasta preguntarlo.
  //
  // Si el regalo venía de convertir una unidad pagada, no se borra: se vuelve
  // a dejar como estaba.
  async function dropGiftLineIfCharged(signature, { converted = false } = {}) {
    const cart = await (await fetch(`${routes.cart_url}.js`)).json();
    const giftLine = findGiftLine(cart);
    if (giftLine && isGiftLineCharged(giftLine)) {
      if (converted) {
        console.error(
          "[free-gift] la Function no ha aceptado la unidad convertida en regalo: vuelve a ser un producto normal",
          { variantId: giftLine.id }
        );
        rejectedSignature = signature;
        await unmarkGiftLine(giftLine);
        return;
      }
      await dropGiftLine(giftLine, signature);
    }
  }

  function refreshUi() {
    document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
    if (typeof publish === "function") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "free-gift" });
    }
  }

  async function evaluate() {
    if (isBusy) {
      queuedRerun = true;
      return;
    }

    const config = getConfig();
    // Regla apagada, sin variante o sin canal válido en la web: no hay alta.
    const canAdd = Boolean(
      config && config.variantId && config.channels && config.channels !== "Solo app"
    );
    if (config) scheduleBoundaryRerun(config);

    isBusy = true;
    const token = ++evaluationToken;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      if (token !== evaluationToken) return;
      if (!cart.items || !cart.items.length) return; // carrito vacío

      const giftLine = findGiftLine(cart);

      // Sin regla no se añade nada, pero un regalo que se esté cobrando se retira
      // igual: apagar la regla no puede dejar al cliente pagando el que ya
      // tuviera puesto (por ejemplo, uno que venga de otro canal o de antes).
      if (!canAdd) {
        if (giftLine && isGiftLineCharged(giftLine)) {
          await removeGiftLine(giftLine);
          refreshUi();
        }
        return;
      }

      // El regalo en el carrito no es ninguno de los configurados: se cambió el
      // regalo en la configuración, o el cliente cambió de mercado. Se retira y
      // se sale; la siguiente evaluación añadirá el que toca. Sin esto, al
      // buscar la línea por la marca (y no por la variante) el regalo viejo
      // contaría como "ya puesto" y el nuevo no se añadiría nunca — y además la
      // Function lo denegaría, porque comprueba la variante marcada.
      //
      // El de reserva cuenta como configurado: si se puso porque el principal
      // no tenía stock, se queda aunque el principal vuelva. Cambiarle el regalo
      // al cliente a mitad de compra sería peor que no hacerlo.
      if (giftLine && !isConfiguredGift(giftLine, config)) {
        await removeGiftLine(giftLine);
        refreshUi();
        // `refreshUi` publica con source "free-gift", que el propio suscriptor
        // ignora para no entrar en bucle, así que hay que encadenar la
        // reevaluación a mano: si no, el regalo nuevo no se añadiría hasta la
        // siguiente acción del cliente.
        queuedRerun = true;
        return;
      }

      const { eligible, anchor, stale } = await evaluateEligibility(cart, config, token);
      if (stale || token !== evaluationToken) return;

      // La firma no incluye la línea del regalo, así que no cambia al añadirla:
      // es la misma antes y después, que es lo que permite usarla como memoria
      // de "este carrito ya fue rechazado".
      const signature = cartSignature(cart);

      if (giftLine) {
        if (!eligible) {
          await removeGiftLine(giftLine);
          refreshUi();
          return;
        }
        // Para el theme sigue cualificando, pero la Function puede haber dejado
        // de aplicarlo tras un cambio posterior del carrito, así que se
        // comprueba en cada pasada y no solo al darlo de alta.
        if (isGiftLineCharged(giftLine)) {
          await dropGiftLine(giftLine, signature);
          refreshUi();
          return;
        }
        if (await replaceSoldOutGift(giftLine, config)) {
          refreshUi();
          return;
        }
        if (shouldUpgradeToMainGift(giftLine, config)) {
          await upgradeToMainGift(cart, giftLine, config);
          refreshUi();
        }
        return;
      }

      if (!eligible || !anchor) return;

      // Este mismo carrito ya provocó que la Function denegara el regalo; no se
      // reintenta hasta que el cliente cambie algo (si no, alta y baja en bucle).
      if (signature === rejectedSignature) return;

      const converted = await convertPaidLineToGift(cart, config, token);
      if (converted === "stale" || token !== evaluationToken) return;
      if (converted) {
        await dropGiftLineIfCharged(signature, { converted: true });
        refreshUi();
        return;
      }

      if (!(await addGiftWithFallback(config))) {
        // Ni el principal ni el de reserva han entrado, normalmente por falta de
        // stock. No se reintenta con este mismo carrito —serían dos altas
        // fallidas en cada evento—; cualquier cambio vuelve a intentarlo.
        rejectedSignature = signature;
        return;
      }
      await dropGiftLineIfCharged(signature);
      refreshUi();
    } catch (error) {
      console.error("[free-gift] evaluation failed", error);
    } finally {
      isBusy = false;
      if (queuedRerun) {
        queuedRerun = false;
        evaluate();
      }
    }
  }

  document.addEventListener("DOMContentLoaded", evaluate);
  document.addEventListener("cart-drawer:refreshed", evaluate);
  if (typeof subscribe === "function") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === "free-gift") return;
      evaluate();
    });
  }
})();
